import { useCallback, useEffect, useState } from 'react';
import { EUserRoles } from '@models/user';
import {
  TInventoryStoreSession,
  TInventoryStoreSessionStatusCounts,
} from '@models/inventoryStoreSession';
import { inventoryStoreSessionApi } from '@services/inventoryStoreSessionApi';
import { appToast } from '@utils/appToast';
import {
  INVENTORY_STORE_SESSION_STATUS,
  isAccountantRole,
  isAreaManagerRole,
} from '@constants/inventoryStoreSession';
import { TApproveReturnDialogType } from '@components/ApproveReturnConfirmDialog/ApproveReturnConfirmDialog';

const MESSAGE = {
  APPROVE_SUCCESS: '承認しました。',
  RETURN_SUCCESS: '差し戻しました。',
  STATUS_UPDATE_ERROR: 'ステータスの更新に失敗しました。再度お試しください。',
  STATUS_UPDATE_FORBIDDEN: 'この操作を行う権限がありません。',
  // VRISU-1228-style race: the session/eligible rows already moved on before this request landed.
  STATUS_CONFLICT_ERROR:
    '既に他のユーザーによってステータスが更新されています。最新の状態に更新しました。',
  // No20_V2 — 100% completion (no 未確認 assets) required before approving/completing.
  CONFIRMATION_RATE_NOT_MET:
    '棚卸確認率が100%に達していないため、承認できません。',
};

// 承認/差し戻し moved from VAMS_PC_009 [4.12] to VAMS_PC_004 header (Option 1, before エクスポート).
// 店舗 = 1 store → single-session update; 店舗 = 全て → bulk update scoped to the caller's role.
function useApprovalPanel(params: {
  role: EUserRoles[] | undefined;
  sessionCode: string; // normalized YYYYMM, '' when 棚卸年月 not selected
  storeCode: string; // '' = 全て
  storeId: string; // selected store's Guid, '' = 全て (used for getBySessionAndStore)
  storeLabel: string; // selected store's display label, for the single-store dialog summary
  totalStoreCount: number; // storesSelect.length, used as bulk dialog's "y" in x/y件
}) {
  const { role, sessionCode, storeCode, storeId, storeLabel, totalStoreCount } =
    params;
  const isAreaManager = isAreaManagerRole(role);
  const isAccountant = isAccountantRole(role);
  const canApprove = isAreaManager || isAccountant;
  const isBulkMode = storeCode === '';

  const [singleSession, setSingleSession] =
    useState<TInventoryStoreSession | null>(null);
  const [statusCounts, setStatusCounts] =
    useState<TInventoryStoreSessionStatusCounts | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<TApproveReturnDialogType>(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!canApprove || !sessionCode) {
      setSingleSession(null);
      setStatusCounts(null);
      return;
    }
    setLoadingStatus(true);
    try {
      if (isBulkMode) {
        const response = await inventoryStoreSessionApi.getStatusCounts(
          sessionCode
        );
        setStatusCounts(response.ok ? response.data : null);
        setSingleSession(null);
      } else {
        const response = await inventoryStoreSessionApi.getBySessionAndStore(
          sessionCode,
          storeId
        );
        setSingleSession(response.ok ? response.data : null);
        setStatusCounts(null);
      }
    } catch {
      setSingleSession(null);
      setStatusCounts(null);
    } finally {
      setLoadingStatus(false);
    }
  }, [canApprove, sessionCode, storeId, isBulkMode]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Each role only ever sees its own stage's count — see BE GetStatusCountsAsync. isAreaManagerRole
  // now excludes ADMIN (AREA_OWNER only), so isAreaManager/isAccountant are already mutually
  // exclusive for a given user — no extra ADMIN-precedence handling needed here.
  // totalPendingCount drives 差し戻し (no completion-rate requirement); completionEligibleCount
  // drives 承認 (No20_V2 — only stores at 100% completion are actually approvable).
  const totalPendingCount = isAccountant
    ? statusCounts?.waitingForAccountantCount ?? 0
    : isAreaManager
    ? statusCounts?.waitingForAreaManagerCount ?? 0
    : 0;
  const completionEligibleCount = isAccountant
    ? statusCounts?.waitingForAccountantEligibleCount ?? 0
    : isAreaManager
    ? statusCounts?.waitingForAreaManagerEligibleCount ?? 0
    : 0;

  const singleStageMatch =
    !!singleSession &&
    ((isAreaManager &&
      singleSession.statusCode ===
        INVENTORY_STORE_SESSION_STATUS.WAITING_FOR_AREA_MANAGER) ||
      (isAccountant &&
        singleSession.statusCode ===
          INVENTORY_STORE_SESSION_STATUS.WAITING_FOR_ACCOUNTANT));
  const singleCompletionMet = singleSession?.isCompletionRateMet ?? false;

  const bulkReturnCanAct = isBulkMode && totalPendingCount > 0;
  const bulkApproveCanAct = isBulkMode && completionEligibleCount > 0;

  const returnDisabled =
    !canApprove ||
    loadingStatus ||
    (isBulkMode ? !bulkReturnCanAct : !singleStageMatch);
  const approveDisabled =
    !canApprove ||
    loadingStatus ||
    (isBulkMode
      ? !bulkApproveCanAct
      : !(singleStageMatch && singleCompletionMet));

  // 承認 disabled specifically because the rate requirement isn't met (vs. wrong stage/no role) —
  // used to show a tooltip explaining why, instead of a silently-disabled button.
  const approveDisabledReason =
    canApprove &&
    !loadingStatus &&
    ((isBulkMode && totalPendingCount > 0 && completionEligibleCount === 0) ||
      (!isBulkMode && singleStageMatch && !singleCompletionMet))
      ? MESSAGE.CONFIRMATION_RATE_NOT_MET
      : null;

  const handleApprove = useCallback(() => {
    setDialogType('APPROVE');
    setDialogOpen(true);
  }, []);

  const handleReturn = useCallback(() => {
    setDialogType('RETURN');
    setDialogOpen(true);
  }, []);

  const handleCancelDialog = useCallback(() => {
    setDialogOpen(false);
    setDialogType(null);
  }, []);

  const handleConfirmDialog = useCallback(async () => {
    if (!dialogType || !sessionCode) {
      return;
    }
    setDialogLoading(true);
    try {
      if (isBulkMode) {
        const targetStatus =
          dialogType === 'APPROVE'
            ? isAccountant
              ? INVENTORY_STORE_SESSION_STATUS.APPROVED
              : INVENTORY_STORE_SESSION_STATUS.WAITING_FOR_ACCOUNTANT
            : INVENTORY_STORE_SESSION_STATUS.RETURNED;
        const response = await inventoryStoreSessionApi.bulkUpdateStatus({
          sessionCode,
          statusCode: targetStatus,
        });
        if (response.ok) {
          const {
            updatedCount,
            eligibleCount: y,
            skippedCount,
            alreadyProcessedCount,
          } = response.data;
          appToast.success({
            message:
              alreadyProcessedCount > 0
                ? `${updatedCount}/${y} 件を更新しました。${alreadyProcessedCount}件は既に処理済みです。`
                : skippedCount > 0
                ? `${updatedCount}/${y} 件を更新しました。${skippedCount}件は棚卸確認率が100%未満のためスキップしました。`
                : dialogType === 'APPROVE'
                ? MESSAGE.APPROVE_SUCCESS
                : MESSAGE.RETURN_SUCCESS,
          });
          handleCancelDialog();
          await fetchStatus();
        } else {
          appToast.error({ message: MESSAGE.STATUS_UPDATE_ERROR });
        }
      } else if (singleSession) {
        const targetStatus =
          dialogType === 'APPROVE'
            ? singleSession.statusCode ===
              INVENTORY_STORE_SESSION_STATUS.WAITING_FOR_AREA_MANAGER
              ? INVENTORY_STORE_SESSION_STATUS.WAITING_FOR_ACCOUNTANT
              : INVENTORY_STORE_SESSION_STATUS.APPROVED
            : INVENTORY_STORE_SESSION_STATUS.RETURNED;
        const response = await inventoryStoreSessionApi.updateStatus(
          singleSession.id,
          { statusCode: targetStatus }
        );
        if (response.ok) {
          appToast.success({
            message:
              dialogType === 'APPROVE'
                ? MESSAGE.APPROVE_SUCCESS
                : MESSAGE.RETURN_SUCCESS,
          });
          handleCancelDialog();
          await fetchStatus();
        } else {
          appToast.error({ message: MESSAGE.STATUS_UPDATE_ERROR });
        }
      }
    } catch (error: any) {
      const errorCode = error?.data?.errors?.[0]?.code;
      if (
        errorCode === 'INVENTORY_STORE_SESSION_STATUS_CONFLICT' ||
        errorCode === 'INVALID_STATUS_TRANSITION'
      ) {
        appToast.error({ message: MESSAGE.STATUS_CONFLICT_ERROR });
        handleCancelDialog();
        await fetchStatus();
      } else if (
        errorCode === 'FORBIDDEN_STATUS_UPDATE_SCOPE' ||
        errorCode === 'FORBIDDEN_BULK_STATUS_UPDATE_SCOPE'
      ) {
        appToast.error({ message: MESSAGE.STATUS_UPDATE_FORBIDDEN });
      } else if (errorCode === 'INSUFFICIENT_CONFIRMATION_RATE') {
        appToast.error({ message: MESSAGE.CONFIRMATION_RATE_NOT_MET });
        handleCancelDialog();
        await fetchStatus();
      } else {
        appToast.error({ message: MESSAGE.STATUS_UPDATE_ERROR });
      }
    } finally {
      setDialogLoading(false);
    }
  }, [
    dialogType,
    sessionCode,
    isBulkMode,
    isAccountant,
    singleSession,
    handleCancelDialog,
    fetchStatus,
  ]);

  return {
    canApprove,
    approveDisabled,
    returnDisabled,
    approveDisabledReason,
    dialogOpen,
    dialogType,
    dialogLoading,
    isBulkMode,
    storeSummary: !isBulkMode ? storeLabel : null,
    bulkSummary: isBulkMode
      ? {
          eligibleCount:
            dialogType === 'APPROVE'
              ? completionEligibleCount
              : totalPendingCount,
          totalCount: totalStoreCount,
        }
      : null,
    handleApprove,
    handleReturn,
    handleCancelDialog,
    handleConfirmDialog,
  };
}

export default useApprovalPanel;
